import React from 'react'

import {
  useFlag,
  DynamicLoadingPage,
  RouteParams,
  useUserProfile,
} from '@sketch/modules-common'
import { PrototypeView, PrototypeViewErrorState } from './PrototypeView'
import { PrototypeViewOld } from './PrototypeViewOld'
import { PrototypeLoadingPlaceholder } from '@sketch/components'
import { usePrototypeQueryParam } from './PrototypeView/hooks'
import { Redirect, useLocation, useParams } from 'react-router'
import { useVersioning } from 'modules/versioning'
import { useGetArtboardPrototypeInfoQuery } from '@sketch/gql-types'
import { addPrototypeQueryParamsToUrlSearchString } from './PrototypeView/utils/addPrototypeQueryParamsToUrlSearchString'
import { LocationDescriptorObject } from 'history'

export function PrototypeViewFFGate() {
  const { loading } = useUserProfile(false)
  const isBetterPrototypeNavBarOn = useFlag('better-prototype-nav-bar')
  const isWebGLPrototypePlayerOn = useFlag('webgl-prototype-player')
  const prototypeStartPointArtboardId = usePrototypeQueryParam()

  if (loading) return <PrototypeLoadingPlaceholder />

  if (isWebGLPrototypePlayerOn) {
    return <RedirectPrototypeViewToPrototypeViewNext />
  }

  if (isBetterPrototypeNavBarOn) {
    if (!prototypeStartPointArtboardId) {
      return <FixMissingPrototypeViewPrototypeQueryParamIfPossible />
    }

    return <PrototypeView />
  } else {
    return <PrototypeViewOld />
  }
}

/**
 * In the past, URLs to prototype artboards did not contain information about what exact prototype
 * was used to reach the displayed artboard. We now have a "prototype" query parameter to track this.
 * We have fixed all the internal URLs to always contain the param but we can still have external URLs
 * that are missing the param.
 *
 * Prototype artboards without the prototype param are considered artboards from an "orphan prototype" , which means
 * we don't really know to what prototype (i.e. prototype starting point) the URL belongs to. (Which means we can't have
 * a proper reset button nor display the name of the prototype)
 *
 * We can't fix all URLs but if the URL points to an artboard that is a start point of a prototype, we consider that prototype
 * to be the opened one.
 */
function FixMissingPrototypeViewPrototypeQueryParamIfPossible() {
  const { artboardUUID } = useParams<RouteParams<'PROTOTYPE_VIEW'>>()
  const { search } = useLocation()
  const versionContext = useVersioning()
  const { getPathname, share, loading: isLoadingVersioning } = versionContext

  const documentId = versionContext.loading
    ? null
    : versionContext.currentVersion.document?.identifier

  const { data, loading } = useGetArtboardPrototypeInfoQuery({
    variables: {
      documentId: documentId ?? '',
      artboardUuid: artboardUUID,
    },
    skip: isLoadingVersioning,
  })

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (!data?.artboardInDocumentWithUuid) {
    return <PrototypeViewErrorState code="artboard_not_found" />
  }

  const isFlowHome = Boolean(data.artboardInDocumentWithUuid.isFlowHome)

  if (!isFlowHome) {
    // Nothing we can do, this will open as a orphan prototype.
    return <PrototypeView />
  }

  const to: LocationDescriptorObject = {
    pathname: getPathname({
      routeKey: 'PROTOTYPE_VIEW',
      routeParams: {
        shareID: share.identifier,
        artboardUUID: artboardUUID,
      },
    }),
    search: addPrototypeQueryParamsToUrlSearchString(search, artboardUUID),
  }

  return <Redirect to={to} push={false} />
}

function RedirectPrototypeViewToPrototypeViewNext() {
  const { artboardUUID } = useParams<RouteParams<'PROTOTYPE_VIEW'>>()
  const { getPathname, share } = useVersioning()
  const to: LocationDescriptorObject = {
    pathname: getPathname({
      routeKey: 'PROTOTYPE_VIEW_NEXT',
      routeParams: {
        shareID: share.identifier,
        prototypeArtboardUUID: artboardUUID,
        currentArtboardUUID: artboardUUID,
      },
    }),
  }
  return <Redirect to={to} push={false} />
}
